// ==============================================
// Dashboard Layout
// ==============================================

import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  HomeIcon,
  CreditCardIcon,
  Cog6ToothIcon,
  DeviceTabletIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  ShieldCheckIcon,
  MapIcon,
  PhotoIcon,
  SignalIcon,
  QrCodeIcon,
  FolderIcon,
  CircleStackIcon,
  DocumentTextIcon,
  ArchiveBoxIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { ProfileDropdown } from '../components/auth/ProfileDropdown';
import { AdminMenuNav } from '../components/admin/AdminMenuNav';
import { useAuth } from '../providers/AuthProvider';
import { api } from '../services/api';
import clsx from 'clsx';

export function DashboardLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Package-gated features are HIDDEN from unentitled accounts, and a plan
  // switch away from a feature must make its entry disappear — strictly the
  // package flag, no existing-content exception (Tony, 2026-08-07). Short
  // staleTime so a plan change reflects on the next navigation/focus. The
  // pages stay mounted with their own gate states for direct URLs.
  const { data: navAccess } = useQuery({
    queryKey: ['nav-access'],
    queryFn: () => api.navAccess(),
    staleTime: 60 * 1000,
  });
  const { data: driveAccess } = useQuery({
    queryKey: ['drive-folders-access'],
    queryFn: () => api.driveFoldersAccess(),
    staleTime: 60 * 1000,
  });
  const { data: papersAccess } = useQuery({
    queryKey: ['papers-access'],
    queryFn: () => api.papersAccess(),
    staleTime: 60 * 1000,
  });
  // Someone asked me for my papers (crew paper requests): /papers is the crew
  // flow's own mobile page, outside this layout.
  const { data: meAccess } = useQuery({
    queryKey: ['papers-me', 'access'],
    queryFn: () => api.papersMeAccess(),
    staleTime: 60 * 1000,
    retry: false,
  });
  const { data: sdAccess } = useQuery({
    queryKey: ['sd-access'],
    queryFn: () => api.sdAccess(),
    staleTime: 60 * 1000,
  });
  const { data: screenshotStorage } = useQuery({
    queryKey: ['screenshot-storage'],
    queryFn: () => api.getScreenshotStorage(),
    staleTime: 60 * 1000,
  });
  const { data: trackingAccess } = useQuery({
    queryKey: ['tracking-access'],
    queryFn: () => api.trackingAccess(),
    staleTime: 60 * 1000,
  });
  const { data: anchorAccess } = useQuery({
    queryKey: ['anchor-remote-access'],
    queryFn: () => api.anchorRemoteAccess(),
    staleTime: 60 * 1000,
  });

  // Storage-related destinations live under one collapsible group to keep
  // the main menu short. Entitlement-gated entries only appear when enabled.
  const storageChildren = [
    ...(driveAccess?.enabled
      ? [{ label: 'Google Drive charts', href: '/dashboard/drive-charts', icon: FolderIcon }]
      : []),
    ...(sdAccess?.enabled
      ? [{ label: 'Manage Scout SD Storage', href: '/dashboard/sd-card', icon: CircleStackIcon }]
      : []),
    { label: 'Downloads', href: '/dashboard/downloads', icon: ArrowDownTrayIcon },
  ];

  type NavEntry =
    | { label: string; href: string; icon: React.ComponentType<React.SVGProps<SVGSVGElement>>; external?: boolean }
    | { label: string; icon: React.ComponentType<React.SVGProps<SVGSVGElement>>; children: typeof storageChildren };

  const navItems: NavEntry[] = [
    { label: 'Overview', href: '/dashboard', icon: HomeIcon },
    { label: 'Devices', href: '/dashboard/devices', icon: DeviceTabletIcon },
    // PassageWiki (formerly "Navigation Library") is the app at /library — the same
    // product entered through the MyPolaris door (one codebase, two doors,
    // 2026-09-11). It is a Worker route on this host, not a route in this app,
    // so it needs a real page load. The old page stays reachable at
    // /dashboard/library while we are sure of the new one.
    ...(navAccess?.enabled
      ? [{ label: 'PassageWiki', href: '/library', icon: MapIcon, external: true }]
      : []),
    ...(papersAccess?.enabled
      ? [{ label: "Ship's Papers", href: '/dashboard/papers', icon: DocumentTextIcon }]
      : []),
    ...(meAccess && meAccess.recipientRequests > 0
      ? [{ label: 'My crew papers', href: '/papers', icon: DocumentTextIcon }]
      : []),
    ...(screenshotStorage?.enabled
      ? [{ label: 'Screenshots', href: '/dashboard/screenshots', icon: PhotoIcon }]
      : []),
    ...(trackingAccess?.enabled
      ? [{ label: 'Tracking', href: '/dashboard/tracking', icon: SignalIcon }]
      : []),
    ...(anchorAccess?.enabled
      ? [{ label: 'Anchor Watch', href: '/dashboard/anchor-watch', icon: QrCodeIcon }]
      : []),
    { label: 'Storage & Downloads', icon: ArchiveBoxIcon, children: storageChildren },
    { label: 'Billing & Plan', href: '/dashboard/billing', icon: CreditCardIcon },
    { label: 'Settings', href: '/dashboard/settings', icon: Cog6ToothIcon },
    // Staff get a "My Timesheet" entry for logging hours.
    ...(user?.is_staff
      ? [{ label: 'My Timesheet', href: '/dashboard/timesheet', icon: ClockIcon }]
      : []),
  ];

  // The group opens itself when one of its pages is active; otherwise the
  // user's last choice is remembered.
  const storageActive = storageChildren.some((c) => location.pathname.startsWith(c.href));
  const [storageOpen, setStorageOpen] = useState(() => {
    try { return localStorage.getItem('dash:nav-storage-open') === '1'; } catch { return false; }
  });
  const storageExpanded = storageOpen || storageActive;
  const toggleStorage = () => {
    const next = !storageExpanded;
    setStorageOpen(next);
    try { localStorage.setItem('dash:nav-storage-open', next ? '1' : '0'); } catch { /* private mode */ }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside className={clsx(
        'w-64 bg-slate-800 border-r border-slate-700 flex flex-col',
        'fixed inset-y-0 left-0 z-50 transition-transform duration-200 ease-in-out md:relative md:translate-x-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        {/* Logo */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img
              src="/vortex-logo-white.svg"
              alt="Vortex Marine"
              className="w-8 h-8"
            />
            <span className="text-xl font-bold text-white">Vortex Marine</span>
          </Link>
          <button
            type="button"
            className="md:hidden p-1 text-slate-400 hover:text-white"
            onClick={closeSidebar}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation — the admin section renders directly below the standard
            menu; the nav scrolls if it outgrows the viewport so nothing is
            ever pushed off-screen. The user card stays pinned below. */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1 px-2">
            {navItems.map((item) => {
              if ('children' in item) {
                return (
                  <li key={item.label}>
                    <button
                      type="button"
                      onClick={toggleStorage}
                      aria-expanded={storageExpanded}
                      className={clsx(
                        'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                        storageActive && !storageExpanded
                          ? 'bg-cyan-500/10 text-cyan-400'
                          : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                      )}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.label}
                      <ChevronDownIcon className={clsx('w-4 h-4 ml-auto transition-transform', storageExpanded && 'rotate-180')} />
                    </button>
                    {storageExpanded && (
                      <ul className="mt-1 space-y-1">
                        {item.children.map((child) => {
                          const childActive = location.pathname.startsWith(child.href);
                          return (
                            <li key={child.href}>
                              <NavLink
                                to={child.href}
                                onClick={closeSidebar}
                                className={clsx(
                                  'flex items-center gap-3 pl-9 pr-3 py-2 rounded-lg text-sm font-medium transition-colors',
                                  childActive
                                    ? 'bg-cyan-500/10 text-cyan-400'
                                    : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                                )}
                              >
                                <child.icon className="w-4 h-4" />
                                {child.label}
                              </NavLink>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              }
              const isActive = item.href === '/dashboard'
                ? location.pathname === '/dashboard'
                : location.pathname.startsWith(item.href);
              const linkClass = clsx(
                'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                isActive
                  ? 'bg-cyan-500/10 text-cyan-400'
                  : 'text-slate-400 hover:bg-slate-700 hover:text-white'
              );

              // An `external` entry lives on this host but not in this app — the
              // Navigation Library is now the PassageWiki app on a Worker route
              // at /library (one codebase, two doors). A NavLink would hand it to
              // this router, which does not have that path; it needs a real load.
              if ('external' in item && item.external) {
                return (
                  <li key={item.href}>
                    <a href={item.href} onClick={closeSidebar} className={linkClass}>
                      <item.icon className="w-5 h-5" />
                      {item.label}
                    </a>
                  </li>
                );
              }

              return (
                <li key={item.href}>
                  <NavLink to={item.href} onClick={closeSidebar} className={linkClass}>
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </NavLink>
                </li>
              );
            })}
          </ul>

          {/* Admin Section (if admin) — the same shared menu as the Admin
              Panel sidebar, so the two can never drift apart. */}
          {isAdmin && (
            <div className="border-t border-slate-700 mt-4 pt-4">
              <div className="px-4 mb-2 flex items-center gap-1.5">
                <ShieldCheckIcon className="w-3.5 h-3.5 text-purple-400" />
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Admin</span>
              </div>
              <div className="px-2">
                <AdminMenuNav accent="purple" onNavigate={closeSidebar} />
              </div>
            </div>
          )}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center gap-3">
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.name || 'Profile'}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-semibold">
                {(user?.name || user?.email || '?')[0].toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-slate-400 truncate">
                {user?.email}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-x-hidden">
        {/* Header */}
        <header className="h-16 border-b border-slate-700 bg-slate-800/50 flex items-center justify-between px-4 sm:px-6">
          <button
            type="button"
            className="md:hidden p-2 text-slate-400 hover:text-white"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="w-6 h-6" />
          </button>
          <div className="hidden md:block" />
          <ProfileDropdown />
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-3 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default DashboardLayout;
